'use client';

/**
 * QuoteCard Component
 *
 * A comprehensive quote summary card with status, totals, and actions.
 * Uses glassmorphism styling with optional neon accent colors.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { QuoteCardProps, QuoteStatus } from '../../types/pricingEngine';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { PriceBreakdown } from '../PriceBreakdown';
import { QuoteLockTimer } from './QuoteLockTimer';

// ============================================================================
// Icon Components
// ============================================================================

const CheckIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const XIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const ChevronDownIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const LockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const RefreshIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// ============================================================================
// Types
// ============================================================================

interface StatusConfig {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  bgLight: string;
  bgDark: string;
}

// ============================================================================
// Constants
// ============================================================================

const STATUS_CONFIG: Record<QuoteStatus, StatusConfig> = {
  DRAFT: {
    label: 'Draft',
    color: 'default',
    bgLight: 'rgba(0, 0, 0, 0.08)',
    bgDark: 'rgba(255, 255, 255, 0.12)',
  },
  PENDING: {
    label: 'Pending',
    color: 'warning',
    bgLight: 'rgba(245, 158, 11, 0.12)',
    bgDark: 'rgba(245, 158, 11, 0.2)',
  },
  SENT: {
    label: 'Sent',
    color: 'info',
    bgLight: 'rgba(59, 130, 246, 0.12)',
    bgDark: 'rgba(59, 130, 246, 0.2)',
  },
  ACCEPTED: {
    label: 'Accepted',
    color: 'success',
    bgLight: 'rgba(16, 185, 129, 0.12)',
    bgDark: 'rgba(16, 185, 129, 0.2)',
  },
  REJECTED: {
    label: 'Rejected',
    color: 'error',
    bgLight: 'rgba(239, 68, 68, 0.12)',
    bgDark: 'rgba(239, 68, 68, 0.2)',
  },
  EXPIRED: {
    label: 'Expired',
    color: 'default',
    bgLight: 'rgba(0, 0, 0, 0.12)',
    bgDark: 'rgba(255, 255, 255, 0.15)',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'default',
    bgLight: 'rgba(0, 0, 0, 0.08)',
    bgDark: 'rgba(255, 255, 255, 0.1)',
  },
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  if (neonStyles) {
    return {
      borderRadius: 16,
      padding: theme.spacing(2),
      background: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
      boxShadow: createNeonGlow($neonAccent!, 'subtle'),
      transition: transitionPresets.card,

      '&:hover': {
        boxShadow: createNeonGlow($neonAccent!, 'medium'),
      },
    };
  }

  return {
    borderRadius: 16,
    padding: theme.spacing(2),
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
    transition: transitionPresets.card,

    '&:hover': {
      background: glass.backgroundHover,
      border: `1px solid ${glass.borderHover}`,
      boxShadow: glass.shadowHover,
    },
  };
});

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
}));

const TotalSection = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: 8,
    background: neonStyles ? neonStyles.background : $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${neonStyles?.border || ($isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')}`,
  };
});

const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const ExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $expanded: boolean }>(({ $expanded }) => ({
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease',
}));

// ============================================================================
// Component
// ============================================================================

export const QuoteCard: React.FC<QuoteCardProps & Omit<BoxProps, 'children'>> = ({
  quote,
  onAccept,
  onReject,
  onExtendLock,
  showDetails = false,
  neonAccent,
  disabled = false,
  ...boxProps
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(showDetails);
  const [loading, setLoading] = useState<'accept' | 'reject' | 'extend' | null>(null);

  const statusConfig = STATUS_CONFIG[quote.status];
  const canAccept = ['PENDING', 'SENT'].includes(quote.status) && !disabled;
  const canReject = ['PENDING', 'SENT'].includes(quote.status) && !disabled;
  const canExtendLock = quote.isLocked && quote.lockExpiresAt && !disabled;

  const handleAccept = async () => {
    if (!onAccept || loading) return;
    setLoading('accept');
    try {
      await onAccept();
    } finally {
      setLoading(null);
    }
  };

  const handleReject = async () => {
    if (!onReject || loading) return;
    setLoading('reject');
    try {
      await onReject();
    } finally {
      setLoading(null);
    }
  };

  const handleExtendLock = async () => {
    if (!onExtendLock || loading) return;
    setLoading('extend');
    try {
      await onExtendLock();
    } finally {
      setLoading(null);
    }
  };

  const getTotalColor = () => {
    if (neonAccent) {
      return getNeonStyles(neonAccent, isDark).base;
    }
    return theme.palette.text.primary;
  };

  return (
    <Box {...boxProps}>
      <StyledCard
        $isDark={isDark}
        $neonAccent={neonAccent}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
      >
        {/* Header */}
        <Header>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Quote #{quote.referenceNumber}
            </Typography>
            {quote.customerName && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {quote.customerName}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {quote.isLocked && (
              <LockIcon
                size={18}
                color={neonAccent ? getNeonStyles(neonAccent, isDark).base : theme.palette.warning.main}
              />
            )}
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: isDark ? statusConfig.bgDark : statusConfig.bgLight,
              }}
            />
          </Box>
        </Header>

        {/* Lock Timer */}
        {quote.isLocked && quote.lockExpiresAt && (
          <Box sx={{ mb: 2 }}>
            <QuoteLockTimer expiresAt={quote.lockExpiresAt} neonAccent={neonAccent} />
          </Box>
        )}

        {/* Summary Info */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Valid From
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(quote.validFrom)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Valid Until
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(quote.validUntil)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Quick Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Subtotal
          </Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(quote.subtotal, quote.currency)}
          </Typography>
        </Box>

        {quote.totalDiscount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Discounts
            </Typography>
            <Typography variant="body2" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums' }}>
              -{formatCurrency(quote.totalDiscount, quote.currency)}
            </Typography>
          </Box>
        )}

        {quote.totalTax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Taxes
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(quote.totalTax, quote.currency)}
            </Typography>
          </Box>
        )}

        {/* Total */}
        <TotalSection $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Total
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: getTotalColor(),
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(quote.total, quote.currency)}
          </Typography>
        </TotalSection>

        {/* Expandable Details */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={
              <ExpandButton $expanded={expanded} size="small">
                <ChevronDownIcon size={18} />
              </ExpandButton>
            }
            sx={{ color: 'text.secondary' }}
          >
            {expanded ? 'Hide Details' : 'Show Details'}
          </Button>

          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              <PriceBreakdown items={quote.lineItems} currency={quote.currency} showSubtotals neonAccent={neonAccent} />

              {quote.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2">{quote.notes}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Actions */}
        {(canAccept || canReject || canExtendLock) && (
          <ActionBar>
            {canAccept && onAccept && (
              <Button
                variant="contained"
                color="success"
                size="medium"
                startIcon={<CheckIcon size={18} />}
                onClick={handleAccept}
                disabled={!!loading}
                sx={{
                  flex: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                  },
                }}
              >
                {loading === 'accept' ? 'Accepting...' : 'Accept Quote'}
              </Button>
            )}

            {canReject && onReject && (
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<XIcon size={18} />}
                onClick={handleReject}
                disabled={!!loading}
                sx={{ flex: 1 }}
              >
                {loading === 'reject' ? 'Rejecting...' : 'Reject'}
              </Button>
            )}

            {canExtendLock && onExtendLock && (
              <Button
                variant="outlined"
                size="medium"
                startIcon={<RefreshIcon size={18} />}
                onClick={handleExtendLock}
                disabled={!!loading}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  borderColor: neonAccent ? getNeonStyles(neonAccent, isDark).border : undefined,
                  color: neonAccent ? getNeonStyles(neonAccent, isDark).base : undefined,
                  '&:hover': {
                    borderColor: neonAccent ? getNeonStyles(neonAccent, isDark).base : undefined,
                  },
                }}
              >
                {loading === 'extend' ? '...' : 'Extend'}
              </Button>
            )}
          </ActionBar>
        )}
      </StyledCard>
    </Box>
  );
};

QuoteCard.displayName = 'QuoteCard';

export default QuoteCard;
